/* eslint-disable no-useless-return */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi, SideBarDef } from "ag-grid-community";
import { useFormikContext } from "formik";
import { Dialog } from "@mui/material";
import { toast } from "react-toastify";
import { Button, ChevronDown, Filter, Search, X } from "./utils/ComponentUtils";
import {
  Box,
  ButtonCancelSecondary,
  ButtonPrimary,
  ButtonPrimary1,
  Center,
  getGrantStrikePrice,
  HStack,
  VStack,
} from "../../../components/utils";
import { Input } from "../../../components/shared/InputField";
import { Popover, PopoverContent, PopoverTrigger } from "./utils/Popover";
import { Select } from "../../../components/shared/Select";
import {
  EmployeeOfferDetails,
  GrantOfferDetails,
  NameAndId,
  TransactionDetailsData,
  WorkflowStepType,
} from "../../../types/Transaction";
import { ChvronDown } from "../../../utils/assets/chevronDown";
import { formatDisplayDate } from "../../../utils/date";
import SearchInput from "../../../components/shared/SearchInput";
import { BulkActions, ExportImport } from "../../esopOverview/GrantsTable";
import { Action, CTADropdown } from "../../../components/shared/Dropdown";
import Avatar from "../../esopOverview/Avatar";
import {
  formatCurrency,
  getCurrencySymbol,
  getCurrencyType,
} from "../../../utils/currencyFormatter";
import {
  useCompleteExerciseTransaction,
  useGetBlackOutDays,
} from "../../../queries/transactionV2";
import { MonetaryEventState, OfferState } from "../../../types/OptionBuyback";
import TransactionStatusLabel from "./utils/TransactionStatusLabel";
import {
  allocateSharesToEmployees,
  changeOfferStatus,
  getTransactionDetails,
  getWillingnessInformation,
  sendReminderEmails,
  resetEmployeeWillingness,
} from "../../../api/TransactionApisV2";
import {
  getEmployeeBlackout,
  getReminderEmailNotes,
  SummaryTransactionCard,
} from "./TransactionStartedDetails";
import DocumentsUploaded from "./utils/DocumentsUploaded";
import WorkflowStepInformationDialog from "./utils/WorkflowStepInformationDialog";
import { getWorkflowStepRequirements } from "./utils/workflowRequirements";
import AddExternalPaymentDetails from "./utils/AddExternalPaymentDetails";
import ApprovePaymentDetails from "./utils/ApprovePaymentDetails";
import CorrectApprovedPaymentDetails from "./utils/CorrectApprovedPaymentDetails";
import PreviewPaymentDetails from "./utils/PreviewPaymentDetails";
import ReminderEmailDialog from "./utils/ReminderEmailDialog";
import AllocateSharesDialog from "./utils/AllocateSharesDialog";
import {
  SummaryCardType,
  SummaryTransactionCards,
} from "./utils/SummaryTransactionCards";
import { ConversionMethodology } from "../../../types/Exercise";
import StatusChangeDialog from "./utils/StatusChangeDialog";
import ResetWillingnessDialog from "./utils/ResetWillingnessDialog";
import DirectRejectDialog from "./utils/DirectRejectDialog";
import { useGetEmailSettings } from "../../../queries/siteSettings";
import AdvancedAgGridSearch from "../../../components/AdvancedAgGridSearch";
import BulkApprovePaymentDetails from "./utils/BulkApprovePaymentDetails";
import BankStatementReconciliationDialog from "./utils/BankStatementReconciliationDialog";
import BulkPaymentUploadDialog from "./BulkPaymentUploadDialog";
import {
  ExercisePaymentDetailsReportDto,
  generateExerciseEventReport,
  generateExercisePaymentDetailsReport,
} from "../../../api/Esop";
import {
  convertBase64ToBlob,
  downloadBlobObject,
} from "../../../utils/DownloadFile";
import PaymentDetailsExportReportDialog from "./utils/PaymentDetailsExportReportDialog";
import { isPaymentAmountComplete } from "../../../utils/paymentCompletion";
import { useIsRulezeroUser } from "../../../utils/useIsRulezeroUser";
import { usePermissionStore } from "../../../store/permissionStore";
import { determineUserAccessToResource } from "../../../utils/auth";
import {
  Action as PermissionAction,
  Resource,
} from "../../../utils/interfaces/Companies";

interface SharesIssuanceDetailsProps {
  onStepChange: (e?: number) => void;
  onBackClick?: () => void;
}

interface FilterType {
  id: string;
  label: string;
  options?: string[];
  type: string;
}

interface Employee {
  id: number;
  name: string;
  position: string;
  hrId: string;
  vestedOptions: number;
  eligibility: number;
  willingness: string;
  status: string;
  avatar: string;
}

interface FilterState {
  department: string[];
  vestedOptions: {
    min: number;
    max?: number;
  };
  eligibleOptions: {
    min: number;
    max?: number;
  };
  employmentType: string[];
  tenure: {
    min: number;
    max?: number;
  };
  participation: string;
}
function isTaxFullyPaidForEmployee(employee: EmployeeOfferDetails): boolean {
  const totalRequired =
    (employee as any)?.transactionCompensationDetails?.totalTaxAmount ?? 0;
  if (totalRequired <= 0) return true;
  const totalApproved = (employee.paymentDetails || [])
    .filter(
      (p) =>
        p.paymentType?.toLowerCase() === "tax price" &&
        p.paymentStatus === "SUCCESS"
    )
    .reduce((s, p) => s + Number(p.amount ?? 0), 0);
  return isPaymentAmountComplete(totalRequired, totalApproved);
}

function isExerciseFullyPaidForEmployee(
  employee: EmployeeOfferDetails
): boolean {
  const totalRequired =
    (employee as any)?.transactionCompensationDetails?.totalStrikePrice ?? 0;
  if (totalRequired <= 0) return true;
  const totalApproved = (employee.paymentDetails || [])
    .filter(
      (p) =>
        p.paymentType?.toLowerCase() === "exercise price" &&
        p.paymentStatus === "SUCCESS"
    )
    .reduce((s, p) => s + Number(p.amount ?? 0), 0);
  return isPaymentAmountComplete(totalRequired, totalApproved);
}

function hasSuccessfulExercisePricePayment(
  employee: EmployeeOfferDetails
): boolean {
  return (employee.paymentDetails || []).some(
    (payment) =>
      payment.paymentType?.toLowerCase() === "exercise price" &&
      payment.paymentStatus === "SUCCESS"
  );
}

function hasFullyPaidSuccessfulExercisePrice(
  employee: EmployeeOfferDetails
): boolean {
  return (
    hasSuccessfulExercisePricePayment(employee) &&
    isExerciseFullyPaidForEmployee(employee)
  );
}

const SharesIssuanceDetails = (props: SharesIssuanceDetailsProps) => {
  const currencySymbol = getCurrencySymbol();
  const currencyType = getCurrencyType();
  // Filter states
  const formik = useFormikContext<TransactionDetailsData>();
  const { permission } = usePermissionStore();
  const canBulkUploadPayments =
    formik.values.monetaryDetails.transactionPaymentEnabled &&
    formik.values.eventState === MonetaryEventState.CLOSED &&
    determineUserAccessToResource(
      permission?.aclList || [],
      Resource.MonetaryTransactionDetails,
      PermissionAction.Create
    );
  const openBulkPaymentUpload = () => {
    if (!formik.values.id) return;
    setBulkPaymentUploadOpen(true);
  };
  const isRulezeroUser = useIsRulezeroUser();

  const handlePaymentSuccess = async () => {
    const response = await getTransactionDetails(formik.values.id);
    formik.setFieldValue("previewList", response.data.previewList);
    formik.setFieldValue(
      "selectedEmployeesList",
      response.data.selectedEmployeesList
    );
  };

  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const { data: allBlackoutDays } = useGetBlackOutDays();

  const [issueSharesDialog, setIssueSharesDialog] = useState<{
    open: boolean;
    employees?: EmployeeOfferDetails[];
  }>({ open: false, employees: undefined });

  const [attachDocumentsDialog, setAttachDocumentsDialog] = useState<{
    open: boolean;
    employeeId?: string;
  }>({ open: false, employeeId: undefined });
  const [willingnessDialog, setWillingnessDialog] = useState<{
    employeeId: string;
    requestId: string;
  } | null>(null);

  const [paymentDialog, setPaymentDialog] = useState<{
    open: boolean;
    approval: boolean;
    correction?: boolean;
    employee?: EmployeeOfferDetails;
  }>({ open: false, approval: false, employee: undefined });

  const [previewPaymentDialog, setPreviewPaymentDialog] = useState<{
    open: boolean;
    employee?: EmployeeOfferDetails;
  }>({ open: false, employee: undefined });

  const [bulkApprovalDialog, setBulkApprovalDialog] = useState<{
    open: boolean;
    paymentType?: "exercise price" | "tax price";
  }>({ open: false });
  const [bankStatementDialogOpen, setBankStatementDialogOpen] = useState(false);
  const [bulkPaymentUploadOpen, setBulkPaymentUploadOpen] = useState(false);

  const [isCompleting, setIsCompleting] = useState(false);
  const [statusDialog, setStatusDialog] = useState<{
    open: boolean;
    employees?: EmployeeOfferDetails[];
  }>({ open: false, employees: undefined });

  const [resetLoading, setResetLoading] = useState(false);

  const [directRejectDialog, setDirectRejectDialog] = useState({
    open: false,
    employee: undefined as EmployeeOfferDetails | undefined,
  });
  const [directRejectLoading, setDirectRejectLoading] = useState(false);

  const gridApi = useRef<GridApi | any>(null);
  const [advancedFilterApi, setAdvancedFilterApi] = useState<any>(null);
  const onGridReady = (params: any) => {
    gridApi.current = params;
    setAdvancedFilterApi(params.api);
  };

  const handleAction = (employee: EmployeeOfferDetails, action: Action) => {
    if (action.disabled) {
      return;
    }
    if (action.name === "Change Status") {
      const blackoutDay = getEmployeeBlackout(
        employee,
        formik.values.monetaryDetails.blackOutDays || [],
        allBlackoutDays || [],
        new Date()
      );
      if (blackoutDay && blackoutDay.exists) {
        toast.error(
          `Employee is in blackout period.${blackoutDay?.reason} Cannot change status at this time.`
        );
        return;
      }
      if (
        (employee.offerState === OfferState.WILLINGNESS_GIVEN ||
          employee.offerState === OfferState.ACCEPTED) &&
        formik.values.id
      ) {
        setStatusDialog({ open: true, employees: [employee] });
      }
    }
    if (action.name === "Allocate Shares" && formik.values.id) {
      if (employee.offerState !== OfferState.PENDING_EXERCISED) {
        toast.error("Employee must be in Pending Exercised state");
        return;
      }
      if (
        formik.values.monetaryDetails.transactionPaymentEnabled &&
        !hasFullyPaidSuccessfulExercisePrice(employee)
      ) {
        toast.error(
          "Exercise price payment must be successful and fully paid before shares can be allocated."
        );
        return;
      }
      const blackoutDay = getEmployeeBlackout(
        employee,
        formik.values.monetaryDetails.blackOutDays || [],
        allBlackoutDays || [],
        new Date()
      );
      if (blackoutDay && blackoutDay.exists) {
        toast.error(
          `Employee is in blackout period.${blackoutDay?.reason} Cannot change status at this time.`
        );
        return;
      }
      if (
        formik.values.monetaryDetails.taxPaymentEnabled &&
        formik.values.monetaryDetails.taxMandatoryForCompletion &&
        !isTaxFullyPaidForEmployee(employee)
      ) {
        toast.error(
          "Tax payment is mandatory and must be fully paid before shares can be allocated."
        );
        return;
      }
      setIssueSharesDialog({ open: true, employees: [employee] });
      return;
    }

    if (action.name === "Attach Documents") {
      setAttachDocumentsDialog({
        open: true,
        employeeId: employee.employeeId,
      });
      return;
    }
    if (action.name === "View willingness information" && employee.requestId) {
      setWillingnessDialog({
        employeeId: employee.employeeId,
        requestId: employee.requestId,
      });
      return;
    }

    if (action.name === "Add Payment Details") {
      if (!formik.values.monetaryDetails.transactionPaymentEnabled) {
        toast.error("Payment details are not enabled for this transaction.");
        return;
      }
      setPaymentDialog({
        open: true,
        approval: false,
        employee,
      });
      return;
    }

    if (action.name === "Approve Payment Details") {
      if (!formik.values.monetaryDetails.transactionPaymentEnabled) {
        toast.error("Payment details are not enabled for this transaction.");
        return;
      }
      setPaymentDialog({
        open: true,
        approval: true,
        employee,
      });
      return;
    }

    if (action.name === "Correct paid payment details") {
      setPaymentDialog({
        open: true,
        approval: false,
        correction: true,
        employee,
      });
      return;
    }

    if (action.name === "Preview Payment Details") {
      setPreviewPaymentDialog({ open: true, employee });
      return;
    }

    if (action.name === "Reject") {
      setDirectRejectDialog({ open: true, employee });
      return;
    }
  };

  const rowData = useMemo(
    () =>
      formik.values.selectedEmployeesList?.map((employee) => ({
        ...employee,
        sharesIssued:
          employee.offerState === OfferState.COMPLETED
            ? employee.grantRelatedDetails?.reduce(
                (sum: number, grant: GrantOfferDetails) =>
                  sum +
                  (grant.conversionMethodology ===
                  ConversionMethodology.ROUND_DOWN
                    ? Math.floor(
                        (grant.willingness || 0) * (grant.conversionRatio || 1)
                      )
                    : Math.ceil(
                        (grant.willingness || 0) * (grant.conversionRatio || 1)
                      )),
                0
              ) || 0
            : 0,
        actions: (
          <CTADropdown
            dropdownClassName=" xl:right-10 lg:right-8 md:right-2"
            actions={[
              {
                name: "Allocate Shares",
                tooltipText:
                  employee.offerState !== OfferState.PENDING_EXERCISED
                    ? "Employee must be in Pending Exercised state"
                    : formik.values.monetaryDetails.transactionPaymentEnabled &&
                      !hasFullyPaidSuccessfulExercisePrice(employee)
                    ? "Exercise price payment must be successful and fully paid before shares can be allocated"
                    : formik.values.monetaryDetails.taxPaymentEnabled &&
                      formik.values.monetaryDetails.taxMandatoryForCompletion &&
                      !isTaxFullyPaidForEmployee(employee)
                    ? "Full tax payment required before shares can be allocated"
                    : "Allocate Shares",
                disabled:
                  employee.offerState !== OfferState.PENDING_EXERCISED ||
                  (formik.values.monetaryDetails.transactionPaymentEnabled &&
                    !hasFullyPaidSuccessfulExercisePrice(employee)) ||
                  (formik.values.monetaryDetails.taxPaymentEnabled &&
                    formik.values.monetaryDetails.taxMandatoryForCompletion &&
                    !isTaxFullyPaidForEmployee(employee)),
              },
              {
                name: "Change Status",
                disabled:
                  employee.offerState !== OfferState.WILLINGNESS_GIVEN &&
                  employee.offerState !== OfferState.ACCEPTED,
                tooltipText:
                  employee.offerState === OfferState.ACCEPTED
                    ? "Move to Pending Exercised or Reject"
                    : "",
              },
              {
                name: "Reject",
                disabled:
                  employee.offerState === OfferState.COMPLETED ||
                  employee.offerState === OfferState.REJECTED,
                tooltipText:
                  employee.offerState === OfferState.COMPLETED
                    ? "Reject not available — offer is already completed"
                    : employee.offerState === OfferState.REJECTED
                    ? "Offer is already rejected"
                    : "Reject exercise request",
              },
              {
                name: "Attach Documents",
                tooltipText: "",
              },
              ...(employee.requestId
                ? [
                    {
                      name: "View willingness information",
                      tooltipText: "View submitted fields and documents",
                    },
                  ]
                : []),
              ...(formik.values.monetaryDetails.transactionPaymentEnabled
                ? [
                    {
                      name: "Add Payment Details",
                      tooltipText: "Payment status",
                    },
                  ]
                : []),
              ...(isRulezeroUser &&
              (employee.paymentDetails || []).some(
                (payment) => payment.paymentStatus === "SUCCESS"
              )
                ? [
                    {
                      name: "Correct paid payment details",
                      tooltipText: "Correct approved payment details",
                    },
                  ]
                : []),
              ...(formik.values.monetaryDetails.transactionPaymentEnabled
                ? [
                    {
                      name: "Approve Payment Details",
                      tooltipText: "Approve Payment Details",
                      // Disabled only when all submitted payments are already resolved
                      disabled:
                        (employee.paymentDetails?.length ?? 0) > 0 &&
                        !employee.paymentDetails?.some(
                          (p) => p.paymentStatus === "PAYMENT_PENDING"
                        ),
                    },
                  ]
                : []),
              ...(formik.values.monetaryDetails.transactionPaymentEnabled
                ? [
                    {
                      name: "Preview Payment Details",
                      tooltipText: "View all payment details",
                      disabled: (employee.paymentDetails?.length ?? 0) === 0,
                    },
                  ]
                : []),
            ]}
            onAction={(action) => handleAction(employee, action)}
          />
        ),
      })) || [],
    [formik.values.selectedEmployeesList]
  );

  const isValidDate = (date: string) =>
    Number.isNaN(date) &&
    new Date(date).toString() !== "Invalid Date" &&
    !Number.isNaN(Date.parse(date));

  const normaliseValue = (value: string | number) => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      if (isValidDate(value)) return new Date(value).valueOf();
      try {
        value = value.replaceAll(",", "");
      } catch (TypeError) {
        value = value.replace(/,./g, "");
      }
      return parseFloat(value);
    }
    return value;
  };

  const sortComparator = (
    valueA: number | string,
    valueB: number | string,
    ..._: any[]
  ) => normaliseValue(valueA) - normaliseValue(valueB);

  const sideBar = useMemo<SideBarDef>(
    () => ({
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          minWidth: 225,
          width: 225,
          maxWidth: 225,
          toolPanelParams: {
            suppressRowGroups: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressValues: true,
          },
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
          minWidth: 180,
          maxWidth: 400,
          width: 250,
        },
      ],
    }),
    []
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      wrapText: true,
      flex: 1,
      autoHeight: true,
      initialWidth: 150,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellClass: "multiline",
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      comparator: sortComparator,
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
    }),
    []
  );

  function PreviewListStatus(status: any) {
    return (
      <HStack>
        <TransactionStatusLabel state={status} />
      </HStack>
    );
  }

  function EmployeeInfoRender(emp: any) {
    return (
      <HStack>
        <Avatar
          name={emp.data.employeeName}
          status={emp.data.employmentStatus}
        />
        <Box className="ml-2">
          <p className="text-xs font-medium text-gray-dark">
            {emp.data.employeeName}
          </p>
          <p className="capitalize text-xxs text-gray-light">
            {emp.data.hrId || ""}
          </p>
        </Box>
      </HStack>
    );
  }

  function RequestedCellRender(params: any) {
    return (
      <VStack className="items-center justify-center gap-1">
        <p
          className={`text-xs  text-gray-dark ${
            params.data?.isTotalRow ? "font-semibold" : "font-medium"
          }`}
        >
          {(params.totalWillingness || 0).toLocaleString(currencyType)}
        </p>
        {!params.data?.isTotalRow && (
          <p className="text-xs font-medium text-gray-400">
            {parseFloat(
              (
                (params.totalWillingness / params.totalEligibleOptions) *
                100
              ).toFixed(4)
            )}
            %
          </p>
        )}
      </VStack>
    );
  }

  const [selectedRows, setSelectedRows] = useState<EmployeeOfferDetails[]>([]);
  const [paymentExportDialogOpen, setPaymentExportDialogOpen] = useState(false);
  const [isExerciseReportDownloading, setIsExerciseReportDownloading] =
    useState(false);

  const onSelectionChanged = (event: any) => {
    setSelectedRows(event.api.getSelectedRows());
  };

  const bulkTargets = (paymentType: "exercise price" | "tax price") =>
    selectedRows.flatMap((employee) =>
      (employee.paymentDetails || [])
        .filter(
          (payment) =>
            payment.paymentStatus === "PAYMENT_PENDING" &&
            payment.paymentType?.toLowerCase() === paymentType &&
            payment.id
        )
        .map((payment) => ({ employee, payment }))
    );

  const canBulkReviewPayments = (paymentType: "exercise price" | "tax price") =>
    selectedRows.length > 0 &&
    selectedRows.every((employee) => {
      const payments = (employee.paymentDetails || []).filter(
        (payment) => payment.paymentType?.toLowerCase() === paymentType
      );

      return (
        payments.length > 0 &&
        payments.every(
          (payment) =>
            payment.paymentStatus === "PAYMENT_PENDING" && Boolean(payment.id)
        )
      );
    });

  const hasPendingBankPayments = (
    formik.values.selectedEmployeesList || []
  ).some((employee) =>
    (employee.paymentDetails || []).some(
      (payment) => payment.paymentStatus === "PAYMENT_PENDING" && payment.id
    )
  );

  const getPaymentDetailsReportParams = (): ExercisePaymentDetailsReportDto => {
    const requestIds = selectedRows
      .map((employee) => employee.requestId)
      .filter((requestId): requestId is string => Boolean(requestId));

    return {
      eventId: formik.values.id || "",
      ...(requestIds.length > 0
        ? { requestIds: Array.from(new Set(requestIds)) }
        : {}),
    };
  };

  const exportPaymentDetails = async (
    reportDto: ExercisePaymentDetailsReportDto
  ) => {
    if (!reportDto.eventId) {
      toast.error("Exercise event details are not available yet.");
      return;
    }
    try {
      const response = await generateExercisePaymentDetailsReport(reportDto);
      const report = response.data;
      const blob = await convertBase64ToBlob(
        report.base64File,
        report.fileType
      );
      downloadBlobObject(blob, "Exercise Payment Details.xlsx");
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Unable to export payment details."
      );
    }
  };

  const exportExerciseEventReport = async (
    reportLevel: "EMPLOYEE" | "GRANT"
  ) => {
    const eventId = formik.values.id;
    if (!eventId || isExerciseReportDownloading) {
      if (!eventId) {
        toast.error("Exercise event details are not available yet.");
      }
      return;
    }

    setIsExerciseReportDownloading(true);
    try {
      const response = await generateExerciseEventReport({
        eventIds: [eventId],
        reportLevel,
      });
      const report = response.data;
      const blob = await convertBase64ToBlob(
        report.base64File,
        report.fileType
      );
      downloadBlobObject(
        blob,
        reportLevel === "GRANT"
          ? "Exercise_Grant_Wise_Report.xlsx"
          : "Exercise_Employee_Wise_Report.xlsx"
      );
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          "Unable to download the exercise report."
      );
    } finally {
      setIsExerciseReportDownloading(false);
    }
  };

  const validBulkActions = [
    ...(canBulkUploadPayments ? [{ name: "Bulk upload payment details" }] : []),
    {
      name: "Allocate Shares",
      disabled:
        selectedRows.length === 0 ||
        selectedRows.some(
          (employee) =>
            employee.offerState !== OfferState.PENDING_EXERCISED ||
            (formik.values.monetaryDetails.transactionPaymentEnabled &&
              !hasFullyPaidSuccessfulExercisePrice(employee))
        ) ||
        (formik.values.monetaryDetails.taxPaymentEnabled &&
          formik.values.monetaryDetails.taxMandatoryForCompletion &&
          selectedRows.some((e) => !isTaxFullyPaidForEmployee(e))),
    },
    {
      name: "Send Exercise Payment Reminders",
      disabled:
        selectedRows.length === 0 ||
        selectedRows.some((e) => isExerciseFullyPaidForEmployee(e)),
    },
    ...(formik.values.monetaryDetails.taxPaymentEnabled
      ? [
          {
            name: "Send Tax Payment Reminders",
            disabled:
              selectedRows.length === 0 ||
              selectedRows.some((e) => isTaxFullyPaidForEmployee(e)),
          },
        ]
      : []),
    {
      name: "Send Letter Sign Reminders",
      disabled:
        !formik.values.monetaryDetails.employeeSignatureRequired ||
        selectedRows.length === 0 ||
        selectedRows.some(
          (e) => e.offerState !== OfferState.WILLINGNESS_ACCEPTED
        ),
    },
    {
      name: "Approve / Reject Exercise Price Payments",
      disabled: !canBulkReviewPayments("exercise price"),
    },
    ...(formik.values.monetaryDetails.taxPaymentEnabled
      ? [
          {
            name: "Approve / Reject Tax Price Payments",
            disabled: !canBulkReviewPayments("tax price"),
          },
        ]
      : []),
    {
      name: "Reconcile bank statement",
      disabled: !hasPendingBankPayments,
    },
  ];

  const [reminderDialog, setReminderDialog] = useState<{
    open: boolean;
    type?: string;
    employees?: EmployeeOfferDetails[];
  }>({ open: false, type: undefined, employees: undefined });

  const [reminderLoading, setReminderLoading] = useState(false);

  const handleSendReminder = async (type?: string) => {
    if (!type) return;
    setReminderLoading(true);
    if (!reminderDialog.employees || reminderDialog.employees.length === 0) {
      toast.error("No employees selected for reminders.");
      setReminderLoading(false);
      return;
    }
    sendReminderEmails({
      employeeIds: reminderDialog.employees.map((emp) => emp.employeeId),
      transactionId: formik.values.id || "",
      templateType: type,
    })
      .then(() => {
        toast.success("Reminders sent!");
        setReminderDialog({
          open: false,
          type: undefined,
          employees: undefined,
        });
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.errorRaw ||
            err?.message ||
            "Failed to send reminders."
        );
      })
      .finally(() => setReminderLoading(false));
  };

  const exercisePricePaid: ColDef | undefined = formik.values.monetaryDetails
    .transactionPaymentEnabled
    ? {
        headerName: "EXERCISE PRICE",
        autoHeight: true,
        colId: "exercisePricePaid",
        valueGetter: (params: any) => {
          const tranches = (params.data?.paymentDetails || []).filter(
            (p: any) => p.paymentType.toLowerCase() === "exercise price"
          );
          const totalRequired =
            params.data?.transactionCompensationDetails?.totalStrikePrice || 0;
          const totalApproved = tranches
            .filter((p: any) => p.paymentStatus === "SUCCESS")
            .reduce((s: number, p: any) => s + (p.amount || 0), 0);
          const isFullyPaid = isPaymentAmountComplete(
            totalRequired,
            totalApproved
          );
          const aggregateStatus = isFullyPaid
            ? "SUCCESS"
            : tranches.some((p: any) => p.paymentStatus === "PAYMENT_PENDING")
            ? "PAYMENT_PENDING"
            : tranches.some((p: any) => p.paymentStatus === "FAILED")
            ? "FAILED"
            : tranches.length > 0
            ? "PENDING"
            : null;
          return {
            amount: totalRequired,
            paymentStatus: aggregateStatus,
            totalApproved,
            trancheCount: tranches.length,
          };
        },
        filter: "agMultiColumnFilter",
        menuTabs: ["filterMenuTab"],
        filterParams: {
          maxNumConditions: 5,
          buttons: ["reset"],
          filters: [
            {
              title: "Amount",
              display: "subMenu",
              filter: "agNumberColumnFilter",
              filterParams: {
                buttons: ["reset"],
                valueGetter: (params: any) =>
                  params.data?.transactionCompensationDetails
                    ?.totalStrikePrice || 0,
              },
            },
            {
              title: "Payment Status",
              display: "subMenu",
              filter: "agSetColumnFilter",
              filterParams: {
                buttons: ["reset"],
                keyCreator: (params: any) => {
                  const s = params.value?.paymentStatus;
                  if (s === "SUCCESS") return "PAID";
                  if (s === "FAILED") return "FAILED";
                  if (s === "PAYMENT_PENDING") return "PENDING APPROVAL";
                  return "NOT SUBMITTED";
                },
                valueFormatter: (params: any) => {
                  const s = params.value?.paymentStatus;
                  if (s === "SUCCESS") return "PAID";
                  if (s === "FAILED") return "FAILED";
                  if (s === "PAYMENT_PENDING") return "PENDING APPROVAL";
                  return "NOT SUBMITTED";
                },
              },
            },
          ],
        },
        flex: 1,
        width: 150,
        cellStyle: { paddingTop: "15px", lineHeight: "20px" },
        cellRenderer: (params: any) => {
          if (params.data?.isTotalRow) {
            return null;
          }
          const paymentDetails = params.data.paymentDetails || [];
          const exerciseTranches = paymentDetails.filter(
            (p: any) => p.paymentType.toLowerCase() === "exercise price"
          );
          const totalRequired =
            params.data.transactionCompensationDetails?.totalStrikePrice || 0;
          const totalApproved = exerciseTranches
            .filter((p: any) => p.paymentStatus === "SUCCESS")
            .reduce((s: number, p: any) => s + (p.amount || 0), 0);
          const isFullyPaid = isPaymentAmountComplete(
            totalRequired,
            totalApproved
          );
          const aggregateStatus = isFullyPaid
            ? "SUCCESS"
            : exerciseTranches.some(
                (p: any) => p.paymentStatus === "PAYMENT_PENDING"
              )
            ? "PAYMENT_PENDING"
            : exerciseTranches.some((p: any) => p.paymentStatus === "FAILED")
            ? "FAILED"
            : null;
          const pendingCount = exerciseTranches.filter(
            (p: any) => p.paymentStatus === "PAYMENT_PENDING"
          ).length;
          return (
            <VStack className="items-center justify-between gap-1">
              <p className="text-xs font-medium text-gray-dark">
                {formatCurrency(totalRequired, currencyType)}
              </p>
              <p
                className={`text-xs font-medium ${
                  aggregateStatus === "FAILED"
                    ? "text-red-500"
                    : aggregateStatus === "SUCCESS"
                    ? "text-[#069F10]"
                    : aggregateStatus === "PAYMENT_PENDING"
                    ? "text-[#E29500]"
                    : params.data.offerState === OfferState.EXPIRED
                    ? ""
                    : "text-gray-400"
                }`}
              >
                {aggregateStatus === "SUCCESS"
                  ? "PAID"
                  : aggregateStatus === "FAILED"
                  ? "FAILED"
                  : aggregateStatus === "PAYMENT_PENDING"
                  ? `PENDING APPROVAL${
                      pendingCount > 1 ? ` (${pendingCount})` : ""
                    }`
                  : params.data.offerState === OfferState.EXPIRED
                  ? ""
                  : "NOT SUBMITTED"}
              </p>
              {!isFullyPaid && totalApproved > 0 && totalRequired > 0 && (
                <div className="w-full mt-0.5">
                  <div className="w-full bg-gray-200 rounded-full h-1">
                    <div
                      className="bg-orange-400 h-1 rounded-full"
                      style={{
                        width: `${Math.min(
                          (totalApproved / totalRequired) * 100,
                          100
                        )}%`,
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {formatCurrency(totalApproved, currencyType)} paid
                  </p>
                </div>
              )}
            </VStack>
          );
        },
      }
    : undefined;

  const taxPaid: ColDef | undefined =
    formik.values.monetaryDetails.taxPaymentEnabled &&
    formik.values.monetaryDetails.transactionPaymentEnabled
      ? {
          headerName: "TAX PRICE",
          autoHeight: true,
          colId: "taxPricePaid",
          valueGetter: (params: any) => {
            const tranches = (params.data?.paymentDetails || []).filter(
              (p: any) => p.paymentType.toLowerCase() === "tax price"
            );
            const totalRequired =
              params.data?.transactionCompensationDetails?.totalTaxAmount || 0;
            const totalApproved = tranches
              .filter((p: any) => p.paymentStatus === "SUCCESS")
              .reduce((s: number, p: any) => s + (p.amount || 0), 0);
            const isFullyPaid = isPaymentAmountComplete(
              totalRequired,
              totalApproved
            );
            const aggregateStatus = isFullyPaid
              ? "SUCCESS"
              : tranches.some((p: any) => p.paymentStatus === "PAYMENT_PENDING")
              ? "PAYMENT_PENDING"
              : tranches.some((p: any) => p.paymentStatus === "FAILED")
              ? "FAILED"
              : tranches.length > 0
              ? "PENDING"
              : null;
            return {
              amount: totalRequired,
              paymentStatus: aggregateStatus,
              totalApproved,
              trancheCount: tranches.length,
            };
          },
          filter: "agMultiColumnFilter",
          menuTabs: ["filterMenuTab"],
          filterParams: {
            maxNumConditions: 5,
            buttons: ["reset"],
            filters: [
              {
                title: "Amount",
                display: "subMenu",
                filter: "agNumberColumnFilter",
                filterParams: {
                  buttons: ["reset"],
                  valueGetter: (params: any) =>
                    params.data?.transactionCompensationDetails
                      ?.totalTaxAmount || 0,
                },
              },
              {
                title: "Payment Status",
                display: "subMenu",
                filter: "agSetColumnFilter",
                filterParams: {
                  buttons: ["reset"],
                  keyCreator: (params: any) => {
                    const s = params.value?.paymentStatus;
                    if (s === "SUCCESS") return "PAID";
                    if (s === "FAILED") return "FAILED";
                    if (s === "PAYMENT_PENDING") return "PENDING APPROVAL";
                    return "NOT SUBMITTED";
                  },
                  valueFormatter: (params: any) => {
                    const s = params.value?.paymentStatus;
                    if (s === "SUCCESS") return "PAID";
                    if (s === "FAILED") return "FAILED";
                    if (s === "PAYMENT_PENDING") return "PENDING APPROVAL";
                    return "NOT SUBMITTED";
                  },
                },
              },
            ],
          },
          flex: 1,
          width: 150,
          cellStyle: { paddingTop: "15px", lineHeight: "20px" },
          cellRenderer: (params: any) => {
            if (params.data?.isTotalRow) {
              return null;
            }
            const paymentDetails = params.data.paymentDetails || [];
            const taxTranches = paymentDetails.filter(
              (p: any) => p.paymentType.toLowerCase() === "tax price"
            );
            const totalRequired =
              params.data.transactionCompensationDetails?.totalTaxAmount || 0;
            const totalApproved = taxTranches
              .filter((p: any) => p.paymentStatus === "SUCCESS")
              .reduce((s: number, p: any) => s + (p.amount || 0), 0);
            const isFullyPaid = isPaymentAmountComplete(
              totalRequired,
              totalApproved
            );
            const aggregateStatus = isFullyPaid
              ? "SUCCESS"
              : taxTranches.some(
                  (p: any) => p.paymentStatus === "PAYMENT_PENDING"
                )
              ? "PAYMENT_PENDING"
              : taxTranches.some((p: any) => p.paymentStatus === "FAILED")
              ? "FAILED"
              : null;
            const pendingCount = taxTranches.filter(
              (p: any) => p.paymentStatus === "PAYMENT_PENDING"
            ).length;
            return (
              <VStack className="items-center justify-between gap-1">
                <p className="text-xs font-medium text-gray-dark">
                  {formatCurrency(totalRequired, currencyType)}
                </p>
                <p
                  className={`text-xs font-medium ${
                    aggregateStatus === "FAILED"
                      ? "text-red-500"
                      : aggregateStatus === "SUCCESS"
                      ? "text-[#069F10]"
                      : aggregateStatus === "PAYMENT_PENDING"
                      ? "text-[#E29500]"
                      : params.data.offerState === OfferState.EXPIRED
                      ? ""
                      : "text-gray-400"
                  }`}
                >
                  {aggregateStatus === "SUCCESS"
                    ? "PAID"
                    : aggregateStatus === "FAILED"
                    ? "FAILED"
                    : aggregateStatus === "PAYMENT_PENDING"
                    ? `PENDING APPROVAL${
                        pendingCount > 1 ? ` (${pendingCount})` : ""
                      }`
                    : params.data.offerState === OfferState.EXPIRED
                    ? ""
                    : "NOT SUBMITTED"}
                </p>
                {!isFullyPaid && totalApproved > 0 && totalRequired > 0 && (
                  <div className="w-full mt-0.5">
                    <div className="w-full bg-gray-200 rounded-full h-1">
                      <div
                        className="bg-orange-400 h-1 rounded-full"
                        style={{
                          width: `${Math.min(
                            (totalApproved / totalRequired) * 100,
                            100
                          )}%`,
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {formatCurrency(totalApproved, currencyType)} paid
                    </p>
                  </div>
                )}
              </VStack>
            );
          },
        }
      : undefined;

  const { mutate: completeTransaction } = useCompleteExerciseTransaction();
  const [date, setDate] = useState<string>("");

  const columnDefs: ColDef[] = [
    {
      headerName: "EMPLOYEE NAME",
      width: 200,
      checkboxSelection: true, // <-- Add this
      headerCheckboxSelection: true,
      autoHeight: true,
      cellStyle: { paddingTop: "15px", lineHeight: "20px" },
      wrapText: true,
      colId: "employeeName",
      valueGetter: (params: any) => ({
        employeeName: params.data?.employeeName,
        employeeDepartment: params.data?.employeeDepartment,
        employeeDesignation: params.data?.employeeDesignation,
        hrId: params.data?.hrId,
      }),
      filter: "agMultiColumnFilter",
      menuTabs: ["filterMenuTab"],
      filterParams: {
        maxNumConditions: 5,
        buttons: ["reset"],
        filters: [
          {
            title: "Name",
            display: "subMenu",
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset"],
              keyCreator: (params: any) => params.value?.employeeName,
              valueFormatter: (params: any) => params.value?.employeeName,
            },
          },
          {
            title: "Department",
            display: "subMenu",
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset"],
              keyCreator: (params: any) => params.value?.employeeDepartment,
              valueFormatter: (params: any) => params.value?.employeeDepartment,
            },
          },
          {
            title: "Designation",
            display: "subMenu",
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset"],
              keyCreator: (params: any) => params.value?.employeeDesignation,
              valueFormatter: (params: any) =>
                params.value?.employeeDesignation,
            },
          },
          {
            title: "Emp ID",
            display: "subMenu",
            filter: "agSetColumnFilter",
            filterParams: {
              buttons: ["reset"],
              keyCreator: (params: any) => params.value?.hrId,
              valueFormatter: (params: any) => params.value?.hrId,
            },
          },
        ],
      },
      comparator(valueA, valueB) {
        return valueA?.employeeName
          ?.toLowerCase()
          ?.localeCompare(valueB?.employeeName?.toLowerCase());
      },
      initialWidth: 200,
      minWidth: 200,
      cellRendererParams: (params: any) => ({
        employeeName: params.data.employeeName,
        employeeDesignation: params.data.employeeDesignation,
        employeeDepartment: params.data.employeeDepartment,
        employeeState: params.data.employeeState,
      }),
      cellRenderer: (params: any) =>
        params.data?.isTotalRow ? "Total" : EmployeeInfoRender(params),
      getQuickFilterText: (params: any) =>
        [
          params.data.employeeName,
          params.data.employeeDesignation,
          params.data.employeeDepartment,
          params.data.employeeState,
          params.data.hrId,
        ]
          .filter(Boolean)
          .join(" "),
    },
    {
      headerName: WorkflowStepType.WILLINGNESS,
      autoHeight: true,
      field: "totalWillingness",
      filter: "agNumberColumnFilter",
      menuTabs: ["filterMenuTab"],
      flex: 1,
      width: 150,
      cellStyle: { paddingTop: "15px", lineHeight: "20px" },

      cellRendererParams: (params: any) => ({
        totalWillingness: params.data.totalWillingness,
        totalEligibleOptions: (() => {
          const percentage =
            formik.values.monetaryDetails.percentageOfNetVestedOptions || 0;
          const totalEligibleOptions = params.data.totalEligibleOptions || 0;

          return percentage > 0
            ? Math.floor(totalEligibleOptions * (percentage / 100))
            : totalEligibleOptions;
        })(),
      }),
      cellRenderer: (params: any) => RequestedCellRender(params),
    },
    ...(exercisePricePaid ? [exercisePricePaid] : []),
    ...(taxPaid ? [taxPaid] : []),
    {
      headerName: "SHARES ISSUED",
      autoHeight: true,
      field: "sharesIssued",
      filter: "agNumberColumnFilter",
      menuTabs: ["filterMenuTab"],
      flex: 1,
      width: 120,
      cellStyle: { paddingTop: "15px", lineHeight: "20px" },
      valueFormatter: (params) =>
        (params.value || 0).toLocaleString(currencyType),
      cellRenderer: (params: any) => {
        const sharesIssued = params.data.sharesIssued || 0;
        return (
          <VStack className="items-center justify-between gap-1">
            <p className="text-xs font-medium text-gray-dark">
              {(sharesIssued || 0).toLocaleString(currencyType)}
            </p>
          </VStack>
        );
      },
    },
    {
      headerName: "STATUS",
      autoHeight: true,
      field: "offerState",
      filter: "agSetColumnFilter",
      menuTabs: ["filterMenuTab"],
      flex: 1,
      width: 80,
      cellStyle: { paddingTop: "15px", lineHeight: "20px" },
      cellRendererParams: (params: any) => ({
        params,
      }),
      cellRenderer: (params: any) => PreviewListStatus(params.value),
    },
    {
      colId: "exerciseDate",
      field: "exerciseDate",
      hide: true,
      filter: "agDateColumnFilter",
    },
    {
      colId: "requestId",
      field: "requestId",
      headerName: "Request ID",
      hide: true,
      filter: "agSetColumnFilter",
    },
    {
      colId: "hrId",
      field: "hrId",
      headerName: "HR ID",
      hide: true,
      filter: "agSetColumnFilter",
    },
    {
      colId: "employeeDepartment",
      field: "employeeDepartment",
      hide: true,
      filter: "agSetColumnFilter",
    },
    {
      colId: "employmentType",
      field: "employmentType",
      hide: true,
      filter: "agSetColumnFilter",
      valueGetter: (params: any) => String(params.data?.employmentType || ""),
    },
    {
      colId: "shareIssuanceStatus",
      hide: true,
      filter: "agSetColumnFilter",
      valueGetter: (params: any) =>
        (params.data?.sharesIssued || 0) > 0 ? "ALLOCATED" : "PENDING",
    },
    {
      headerName: "",
      field: "actions",
      pinned: "right",
      hide: false,
      width: 80,
      maxWidth: 80,
      filter: false,
      colId: "action-column",
      suppressNavigable: true,
      suppressColumnsToolPanel: true,
      resizable: false,
      sortable: false,
      autoHeight: true,
      menuTabs: ["columnsMenuTab"],
      cellRendererParams: ({ value }: { value: { props: any } }) =>
        value ? value.props : null,
      cellRenderer: CTADropdown,
    },
  ];

  const handleBackClick = () => {
    if (props.onBackClick) {
      props.onBackClick();
    }
  };

  const handleCompleteTransaction = () => {
    setIsCompleting(true);
    if (
      formik.values.selectedEmployeesList?.some(
        (e) => e.offerState === OfferState.PENDING_EXERCISED
      )
    ) {
      toast.error(
        "Please allocate shares to all employees with pending exercise status before completing the transaction."
      );
      setIsCompleting(false);
      return;
    }
    completeTransaction(
      {
        transactionId: formik.values.id,
      },
      {
        onSuccess: (data) => {
          formik.setFieldValue("eventState", MonetaryEventState.COMPLETED);
          formik.setFieldValue("previewList", data.previewList);
          formik.setFieldValue(
            "selectedEmployeesList",
            data.selectedEmployeesList
          );
          setIsCompleting(false);
        },
        onError: (error: any) => {
          toast.error(
            error.response?.data?.reason || "Failed to complete transaction"
          );
          setIsCompleting(false);
        },
      }
    );
  };

  const [statusLoading, setStatusLoading] = useState(false);
  const [metaData, setMetaData] = useState<any>();

  const handleStatusChange = (status: OfferState) => {
    if (!statusDialog.employees) return;
    setStatusLoading(true);
    changeOfferStatus({
      employees: statusDialog.employees.map((emp) => emp.employeeId),
      requestIds: statusDialog.employees
        .map((emp) => emp.requestId)
        .filter(Boolean) as string[],
      transactionId: formik.values.id || "",
      status,
      metaData,
    })
      .then((response: any) => {
        setStatusDialog({ open: false, employees: undefined });
        formik.setFieldValue("previewList", response.data.previewList);
        formik.setFieldValue(
          "selectedEmployeesList",
          response.data.selectedEmployeesList
        );
        toast.success("Status updated!");
      })
      .catch((err) => {
        toast.error(err?.response?.data?.errorRaw || "Failed to update status");
      })
      .finally(() => setStatusLoading(false));
  };

  function setPinnedBottomRowData({ api }: { api: any }) {
    const data = api.rowModel.rootNode;
    const filteredData = data.childrenAfterAggFilter.map(
      (element: any) => element.data
    );

    const totalVestedOptions = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.totalVestedOptions,
      0
    );
    const totalEligibility = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.totalEligibleOptions,
      0
    );
    const totalWillingness = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.totalWillingness,
      0
    );
    const totalSharesIssued = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.sharesIssued,
      0
    );

    api.setPinnedBottomRowData([
      {
        employeeName: "Total",
        totalVestedOptions,
        totalEligibleOptions: totalEligibility,
        totalWillingness,
        sharesIssued: totalSharesIssued,
        isTotalRow: true,
      },
    ]);
  }

  const { data: emailSettings } = useGetEmailSettings();

  return (
    <VStack className="w-full">
      <HStack className="justify-between gap-1 pb-3 px-6 font-medium border-b-[0.5px] pt-7">
        <VStack>
          <HStack className="flex justify-between text-xl font-semibold text-black-501">
            Share Issuance
          </HStack>
          <HStack className="flex text-sm font-medium text-gray-500">
            Allocate shares to participants who have successfully completed
            payment and signed the required documents. Review final details
            before confirming the allocation.
          </HStack>
        </VStack>
        <VStack className="items-end">
          <HStack className="flex justify-between text-sm font-semibold text-black-501">
            {formatDisplayDate(formik.values.endDate)}
          </HStack>
          <HStack className="flex ml-10 text-xs font-medium text-gray-500">
            Last Date to Accept
          </HStack>
        </VStack>
      </HStack>
      <Dialog
        open={issueSharesDialog.open}
        onClose={() =>
          setIssueSharesDialog({ open: false, employees: undefined })
        }
        maxWidth="lg"
        PaperProps={{ style: { minWidth: "900px" } }}
      >
        <AllocateSharesDialog
          employees={issueSharesDialog.employees}
          onClose={() =>
            setIssueSharesDialog({ open: false, employees: undefined })
          }
        />
      </Dialog>
      <StatusChangeDialog
        open={statusDialog.open}
        employees={statusDialog.employees}
        onClose={() => {
          setMetaData(undefined);
          setStatusDialog({ open: false, employees: undefined });
        }}
        onSubmit={handleStatusChange}
        loading={statusLoading}
        setMetaData={setMetaData}
        metaData={metaData}
      />
      <Dialog
        open={paymentExportDialogOpen}
        onClose={() => setPaymentExportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        {paymentExportDialogOpen && (
          <PaymentDetailsExportReportDialog
            params={getPaymentDetailsReportParams()}
            onClose={() => setPaymentExportDialogOpen(false)}
            onExport={(reportDto) => {
              setPaymentExportDialogOpen(false);
              exportPaymentDetails(reportDto);
            }}
          />
        )}
      </Dialog>
      <Dialog
        open={attachDocumentsDialog.open}
        onClose={() =>
          setAttachDocumentsDialog({ open: false, employeeId: undefined })
        }
        maxWidth="lg"
      >
        <DocumentsUploaded
          employeeId={attachDocumentsDialog.employeeId || ""}
          transactionId={formik.values.id || ""}
          onClose={() =>
            setAttachDocumentsDialog({ open: false, employeeId: undefined })
          }
        />
      </Dialog>
      {willingnessDialog && (
        <WorkflowStepInformationDialog
          employeeId={willingnessDialog.employeeId}
          eventId={formik.values.id || ""}
          requestId={willingnessDialog.requestId}
          stepLabel="Willingness"
          loadInformation={getWillingnessInformation}
          requirements={getWorkflowStepRequirements(
            formik.values.monetaryDetails.workflowSteps,
            WorkflowStepType.WILLINGNESS
          )}
          onClose={() => setWillingnessDialog(null)}
        />
      )}
      <Dialog
        open={paymentDialog.open}
        onClose={() =>
          setPaymentDialog({
            open: false,
            approval: false,
            employee: undefined,
          })
        }
        maxWidth={paymentDialog.correction ? "xl" : "lg"}
        fullWidth={paymentDialog.correction}
      >
        {paymentDialog.correction ? (
          <CorrectApprovedPaymentDetails
            monetaryDetails={formik.values.monetaryDetails}
            employee={paymentDialog.employee}
            transactionId={formik.values.id}
            onSuccess={handlePaymentSuccess}
            onClose={() =>
              setPaymentDialog({
                open: false,
                approval: false,
                correction: false,
                employee: undefined,
              })
            }
          />
        ) : paymentDialog.approval ? (
          <ApprovePaymentDetails
            monetaryDetails={formik.values.monetaryDetails}
            employee={paymentDialog.employee}
            transactionId={formik.values.id}
            onClose={() =>
              setPaymentDialog({
                open: false,
                approval: false,
                employee: undefined,
              })
            }
            onSuccess={(data) => {
              formik.setFieldValue("previewList", data.previewList);
              formik.setFieldValue(
                "selectedEmployeesList",
                data.selectedEmployeesList
              );
            }}
            paymentDetails={paymentDialog.employee?.paymentDetails || []}
          />
        ) : (
          <AddExternalPaymentDetails
            monetaryDetails={formik.values.monetaryDetails}
            employee={paymentDialog.employee}
            transactionId={formik.values.id}
            onSuccess={handlePaymentSuccess}
            onClose={() =>
              setPaymentDialog({
                open: false,
                approval: false,
                employee: undefined,
              })
            }
            paymentDetails={paymentDialog.employee?.paymentDetails || []}
          />
        )}
      </Dialog>
      <Dialog
        open={previewPaymentDialog.open}
        onClose={() =>
          setPreviewPaymentDialog({ open: false, employee: undefined })
        }
        maxWidth="lg"
      >
        <PreviewPaymentDetails
          monetaryDetails={formik.values.monetaryDetails}
          employee={previewPaymentDialog.employee}
          onClose={() =>
            setPreviewPaymentDialog({ open: false, employee: undefined })
          }
          paymentDetails={previewPaymentDialog.employee?.paymentDetails || []}
        />
      </Dialog>
      <Dialog
        open={bulkApprovalDialog.open}
        onClose={() => setBulkApprovalDialog({ open: false })}
        maxWidth="xl"
      >
        {bulkApprovalDialog.paymentType && formik.values.id && (
          <BulkApprovePaymentDetails
            transactionId={formik.values.id}
            paymentType={bulkApprovalDialog.paymentType}
            targets={bulkTargets(bulkApprovalDialog.paymentType)}
            onClose={() => setBulkApprovalDialog({ open: false })}
            onSuccess={(data) => {
              formik.setFieldValue("previewList", data.previewList);
              formik.setFieldValue(
                "selectedEmployeesList",
                data.selectedEmployeesList
              );
            }}
          />
        )}
      </Dialog>
      <Dialog
        open={bankStatementDialogOpen}
        onClose={() => setBankStatementDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { maxHeight: "calc(100vh - 32px)" } }}
      >
        {bankStatementDialogOpen && formik.values.id && (
          <BankStatementReconciliationDialog
            transactionId={formik.values.id}
            currencyType={currencyType}
            onClose={() => setBankStatementDialogOpen(false)}
            onSuccess={handlePaymentSuccess}
          />
        )}
      </Dialog>
      {bulkPaymentUploadOpen && formik.values.id && (
        <BulkPaymentUploadDialog
          eventId={formik.values.id}
          eventName={formik.values.eventName}
          onClose={() => setBulkPaymentUploadOpen(false)}
          onSuccess={handlePaymentSuccess}
        />
      )}
      <div className="w-full px-6 pb-8">
        <VStack className="px-6 py-4 ">
          <HStack className="flex gap-4 pt-6 text-base font-semibold text-center text-black-501">
            <SummaryTransactionCards
              cardTypes={
                [
                  "employees",
                  "optionsExercised",
                  "exercisePricePaid",
                  "shares",
                ] as SummaryCardType[]
              }
              currencyType={currencyType}
            />
          </HStack>
        </VStack>
      </div>

      <VStack aria-label="toolbar" className="w-full gap-4 px-6 pt-4">
        <HStack className="items-center justify-end w-full h-9 text-xs text-center whitespace-nowrap">
          <Center className="order-1">
            <BulkActions
              actions={validBulkActions}
              onAction={(action) => {
                if (action.disabled) return;

                if (action.name === "Bulk upload payment details") {
                  openBulkPaymentUpload();
                } else if (action.name === "Allocate Shares") {
                  setIssueSharesDialog({ open: true, employees: selectedRows });
                } else if (action.name === "Send Exercise Payment Reminders") {
                  setReminderDialog({
                    open: true,
                    type: "ExerciseStrikePriceReminderEmployee",
                    employees: selectedRows,
                  });
                } else if (action.name === "Send Tax Payment Reminders") {
                  setReminderDialog({
                    open: true,
                    type: "ExerciseTaxPriceReminderEmployee",
                    employees: selectedRows,
                  });
                } else if (action.name === "Send Letter Sign Reminders") {
                  setReminderDialog({
                    open: true,
                    type: "ExerciseLetterSignReminderEmployee",
                    employees: selectedRows,
                  });
                } else if (
                  action.name === "Approve / Reject Exercise Price Payments"
                ) {
                  setBulkApprovalDialog({
                    open: true,
                    paymentType: "exercise price",
                  });
                } else if (
                  action.name === "Approve / Reject Tax Price Payments"
                ) {
                  setBulkApprovalDialog({
                    open: true,
                    paymentType: "tax price",
                  });
                } else if (action.name === "Reconcile bank statement") {
                  setBankStatementDialogOpen(true);
                }
              }}
            />
          </Center>
          <div className="order-2">
            <ExportImport
              actions={[
                {
                  name: "Download Employee-wise Exercise Report",
                  disabled: isExerciseReportDownloading,
                },
                {
                  name: "Download Grant-wise Exercise Report",
                  disabled: isExerciseReportDownloading,
                },
                { name: "Export Payment Details" },
              ]}
              onAction={(action) => {
                if (action.disabled) return;
                if (action.name === "Export Payment Details") {
                  setPaymentExportDialogOpen(true);
                } else if (
                  action.name === "Download Employee-wise Exercise Report"
                ) {
                  exportExerciseEventReport("EMPLOYEE");
                } else if (
                  action.name === "Download Grant-wise Exercise Report"
                ) {
                  exportExerciseEventReport("GRANT");
                }
              }}
            />
          </div>
        </HStack>
        <HStack className="items-center justify-between w-full">
          <div className="w-[360px] max-w-full">
            <SearchInput
              className=""
              placeholder="Search"
              onChange={(e: any) => {
                gridApi.current.api.setQuickFilter(e.target.value);
              }}
            />
          </div>
          {advancedFilterApi && (
            <AdvancedAgGridSearch
              gridApi={advancedFilterApi}
              tableIdentifier="shareIssuanceTable"
              allowedColumns={["requestId", "hrId", "offerState"]}
              defaultColumn="hrId"
            />
          )}
        </HStack>
      </VStack>
      {/* AG Grid table */}
      <HStack className="justify-between w-full pt-4">
        <Box
          style={{
            height: `${
              rowData.length <= 3
                ? "300"
                : rowData.length >= 10
                ? "600"
                : rowData.length * 100
            }px`,
          }}
          className="w-full h-full max-h-full overflow-x-auto bg-black ag-theme-material"
        >
          <AgGridReact
            getRowStyle={(params) => {
              if (params.data?.isTotalRow) {
                return {
                  fontWeight: 600,
                };
              }
              return undefined;
            }}
            onRowDataUpdated={setPinnedBottomRowData}
            rowData={rowData}
            onGridReady={onGridReady}
            rowSelection="multiple"
            onSelectionChanged={onSelectionChanged}
            columnDefs={columnDefs}
            suppressRowTransform={true}
            sideBar={sideBar}
            defaultColDef={defaultColDef}
            animateRows={true}
            suppressRowClickSelection
            suppressMenuHide={true}
            pagination={true}
            rowClass={
              "border-t border-dashed cursor-pointer hover:bg-slate-50 "
            }
            overlayNoRowsTemplate={
              '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;   margin-top: 50px;">No Rows To Show</span>'
            }
          ></AgGridReact>
        </Box>
      </HStack>
      <Dialog
        open={reminderDialog.open}
        onClose={() =>
          setReminderDialog({
            open: false,
            type: undefined,
            employees: undefined,
          })
        }
        maxWidth="lg"
      >
        <ReminderEmailDialog
          open={reminderDialog.open}
          employees={reminderDialog.employees}
          onClose={() =>
            setReminderDialog({
              open: false,
              type: undefined,
              employees: undefined,
            })
          }
          onSubmit={() => handleSendReminder(reminderDialog.type)}
          loading={reminderLoading}
          notesElement={getReminderEmailNotes(emailSettings)}
        />
      </Dialog>

      <HStack className="justify-between w-full p-6">
        <ButtonPrimary1 onClick={handleBackClick}>Back</ButtonPrimary1>

        {formik.values.eventState === MonetaryEventState.CLOSED ? (
          <HStack className="gap-4">
            <ButtonPrimary1
              onClick={handleCompleteTransaction}
              loading={isCompleting}
            >
              Complete
            </ButtonPrimary1>
          </HStack>
        ) : (
          <HStack className="gap-4">
            <ButtonPrimary
              onClick={() => {
                props.onStepChange();
              }}
            >
              Next
            </ButtonPrimary>
          </HStack>
        )}
      </HStack>
      <DirectRejectDialog
        open={directRejectDialog.open}
        employeeName={directRejectDialog.employee?.employeeName}
        onClose={() =>
          setDirectRejectDialog({ open: false, employee: undefined })
        }
        loading={directRejectLoading}
        onSubmit={async (reason) => {
          if (!directRejectDialog.employee) return;
          setDirectRejectLoading(true);
          try {
            const res = await resetEmployeeWillingness({
              transactionId: formik.values.id || "",
              employeeIds: [directRejectDialog.employee.employeeId],
              requestIds: directRejectDialog.employee.requestId
                ? [directRejectDialog.employee.requestId]
                : undefined,
              reason,
            });
            formik.setFieldValue("previewList", res.data.previewList);
            formik.setFieldValue(
              "selectedEmployeesList",
              res.data.selectedEmployeesList
            );
            toast.success("Exercise request rejected successfully.");
            setDirectRejectDialog({ open: false, employee: undefined });
          } catch (err: any) {
            const msg =
              err?.response?.data?.errorRaw ||
              err?.response?.data?.message ||
              err?.message ||
              "Failed to reject exercise request";
            toast.error(msg);
          } finally {
            setDirectRejectLoading(false);
          }
        }}
      />
    </VStack>
  );
};

export default SharesIssuanceDetails;
